import { Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, Paper, Tooltip } from '@pulse/ui';
import type { ReactNode } from 'react';

import type { PanelPosition } from '../types/notification.types';

import { useFloatingPanel } from './FloatingPanelContext';

export interface FloatingPanelProps {
  header?: ReactNode;
  footer?: ReactNode;
  content?: ReactNode;
  /** Icon shown as the circular FAB on mobile (e.g. bell with badge) */
  fab?: ReactNode;
  /** Position of the panel on screen (default: "bottom-right") */
  position?: PanelPosition;
  /** Distance from the bottom of the screen in px (default: 24) */
  bottom?: number;
  /** Distance from the side of the screen in px (default: 24) */
  side?: number;
}

// Generic reusable floating container — has no notion of "notifications" itself. Any
// content can be slotted in via header/content/footer/fab, so this same shell can host
// pending approvals, system alerts, or other future widgets, not just notifications.
export function FloatingPanelImplementation({
  header,
  footer,
  content,
  fab,
  position = 'bottom-right',
  bottom = 24,
  side = 24,
}: FloatingPanelProps) {
  const { title, open, minimized, onMinimize, onClose } = useFloatingPanel();

  if (!open) return null;

  const isRight = position === 'bottom-right';
  const anchor = isRight ? { right: side } : { left: side };

  // ── FAB (collapsed state, every breakpoint)
  if (minimized) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom,
          ...anchor,
          // Below `modal` (Dialogs) so a dialog opened over this corner of the screen always
          // receives clicks — this panel is a persistent widget, not a transient toast, and
          // must never compete with modal content for pointer events.
          zIndex: (t) => t.zIndex.modal - 1,
          display: 'inline-flex',
          // focus-within, not just hover — the close button is the only way to dismiss the
          // panel while collapsed, so it has to be reachable by keyboard too, not only by
          // pointer. Both selectors drive the same reveal below.
          '&:hover .FloatingPanel-fabClose, &:focus-within .FloatingPanel-fabClose': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        <Paper
          elevation={6}
          role="button"
          tabIndex={0}
          aria-label={`Expand ${title} panel`}
          onClick={onMinimize}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onMinimize();
            }
          }}
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            transition: 'background-color 0.2s',
          }}
        >
          {fab}
        </Paper>

        <Tooltip title="Close" placement="top">
          {/* Top-*left* corner: the unread badge already occupies the FAB's top-right (see
              NotificationFab.tsx), so a close button there would sit on top of the count. */}
          <IconButton
            className="FloatingPanel-fabClose"
            size="small"
            aria-label={`Close ${title} panel`}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{
              position: 'absolute',
              top: -4,
              left: -4,
              p: 0.25,
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.15s',
              bgcolor: 'background.paper',
              color: 'text.secondary',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper', color: 'text.primary' },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // ── Full panel (expanded state)
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom,
        ...anchor,
        width: { xs: `calc(100vw - ${side * 2}px)`, sm: 360 },
        maxWidth: 360,
        // See the FAB Paper above for why this stays below `modal`.
        zIndex: (t) => t.zIndex.modal - 1,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* HEADER */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>{header}</Box>

      {/* BODY + FOOTER. No collapse wrapper here: the minimized state returns the FAB branch
          above at every breakpoint, so this branch only ever renders expanded. That also keeps
          the old guarantee that notification content stays out of the DOM entirely while
          collapsed, rather than every page holding the last 20 notifications' text hidden. */}
      {content}
      {footer}
    </Paper>
  );
}
